/* ==========================================================================
   Rook UI Core — Sheet Controller
   Web Component: <rk-sheet-element>
   Hydration tier: 'interaction' (external trigger pattern)
   ========================================================================== */

import { RkComponent } from '@rook/component';

function waitForExitAnimation(el, done) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || reduce) {
        done();
        return () => {};
    }

    let settled = false;
    const onEnd = (e) => {
        if (e.target !== el) return;
        finish();
    };
    const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        done();
    };
    const cancel = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
    };
    el.addEventListener('transitionend', onEnd);
    const timer = setTimeout(finish, 500);
    return cancel;
}

class RkSheetElement extends RkComponent {
    static hydration = 'interaction';

    #cancelExitAnim = null;
    #keydownController = null;
    #triggerCleanups = [];

    attachHydrationTrigger(hydrate) {
        const sheet = this.querySelector('.rk-sheet');
        const id = this.dataset.sheetId || sheet?.id;
        if (!id) return () => {};

        const triggers = document.querySelectorAll(`[data-sheet-open="${id}"]`);
        const cleanups = [];

        triggers.forEach((trigger) => {
            const onClick = (event) => {
                event.preventDefault();
                trigger.removeEventListener('click', onClick);
                hydrate();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            cleanups.push(() => trigger.removeEventListener('click', onClick));
        });

        return () => cleanups.forEach((fn) => fn());
    }

    setup() {
        this.sheet = this.querySelector('.rk-sheet');
        this.overlay = this.querySelector('.rk-sheet__overlay');
        this.panel = this.querySelector('.rk-sheet__panel');
        this.previousFocus = null;

        if (!this.sheet || !this.panel) return;

        const overlayClose = this.dataset.overlayClose !== 'false';

        const delegateMap = {
            'click [data-action="close"]': () => this.close(),
        };
        if (overlayClose && this.overlay) {
            delegateMap['click .rk-sheet__overlay'] = (event, target) => {
                if (event.target === target) this.close();
            };
        }
        this.delegate(delegateMap);

        this.#setupPersistentOpenTriggers();
    }

    teardown() {
        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }
        if (this.#keydownController) {
            this.#keydownController.abort();
            this.#keydownController = null;
        }
        this.#triggerCleanups.forEach((fn) => fn());
        this.#triggerCleanups = [];
    }

    #setupPersistentOpenTriggers() {
        const sheetId = this.dataset.sheetId || this.sheet?.id;
        if (!sheetId) return;

        const triggers = document.querySelectorAll(`[data-sheet-open="${sheetId}"]`);
        triggers.forEach((trigger) => {
            const onClick = (e) => {
                e.preventDefault();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            this.#triggerCleanups.push(() => trigger.removeEventListener('click', onClick));
        });
    }

    isOpen() {
        return this.sheet?.classList.contains('rk-sheet--active');
    }

    open() {
        if (!this.sheet) return;

        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }

        if (this.isOpen() && this.sheet.dataset.state !== 'closed') return;

        this.previousFocus = document.activeElement;
        this.sheet.classList.add('rk-sheet--active');
        this.sheet.dataset.state = 'open';
        document.body.style.overflow = 'hidden';

        this.#keydownController?.abort();
        this.#keydownController = new AbortController();
        document.addEventListener('keydown', (e) => this.#onKeyDown(e), { signal: this.#keydownController.signal });

        requestAnimationFrame(() => {
            const firstFocusable = this.panel.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
                firstFocusable.focus();
            }
        });

        this.rkEmit('sheet:open', { id: this.sheet.id });
    }

    close() {
        if (!this.sheet || !this.isOpen()) return;
        if (this.sheet.dataset.state === 'closed') return;

        this.sheet.dataset.state = 'closed';

        const finalize = () => {
            this.#cancelExitAnim = null;
            this.sheet.classList.remove('rk-sheet--active');
            delete this.sheet.dataset.state;
            document.body.style.overflow = '';

            this.#keydownController?.abort();
            this.#keydownController = null;

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.rkEmit('sheet:close', { id: this.sheet.id });
        };

        this.#cancelExitAnim = waitForExitAnimation(this.panel, finalize);
    }

    #onKeyDown(e) {
        if (!this.isOpen()) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
            return;
        }

        if (e.key === 'Tab') {
            this.#trapFocus(e);
        }
    }

    #trapFocus(e) {
        const focusable = this.panel?.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (!focusable || focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }
}

if (!customElements.get('rk-sheet-element')) {
    customElements.define('rk-sheet-element', RkSheetElement);
}
